import { useState, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { type TimelineEvent, getSwimLaneColor } from './timeline-utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { useIsMobile } from '@/lib/hooks/use-mobile';

interface EventMarkerProps {
    event: TimelineEvent;
    startPosition: number; // pixel position for start year
    endPosition: number; // pixel position for end year
    rowIndex: number; // which row within the lane (for vertical offset)
    onClick: (event: TimelineEvent) => void;
}

// Static icon map - moved outside component to avoid recreation
const ICON_MAP: Record<string, string> = {
    'biblical-composition': '📜',
    'biblical-narrative': '📖',
    'israel-ruler': '👑',
    'israel-prophet': '📣',
    'empire': '🏛️',
    'people': '🏘️',
    'political': '⚖️',
    'legal': '⚖️',
    'military': '⚔️',
    'religious': '🙏',
    'cultural': '🎭',
    'economic': '💰',
    'technological': '⚙️',
    'natural': '🌊',
    'diaspora': '🚶'
};

// Static constants
const TITLE_REPEAT_INTERVAL = 600; // Repeat title every 600px
const CUSTOM_TOOLTIP_THRESHOLD = 50; // Use custom tooltip for bars longer than this

/**
 * Get icon for event based on its primary category
 */
function getEventIcon(event: TimelineEvent): string | null {
    const primaryCategory = event.categories[0]?.category;
    return ICON_MAP[primaryCategory] || null;
}

/**
 * Memoized component for repeated event labels
 */
const EventLabels = memo(({
    event,
    eventIcon,
    width,
    isLongBar,
    titleRepeatCount
}: {
    event: TimelineEvent;
    eventIcon: string | null;
    width: number;
    isLongBar: boolean;
    titleRepeatCount: number;
}) => {
    if (isLongBar) {
        // For long bars: repeat title at intervals
        return (
            <>
                {Array.from({ length: titleRepeatCount }).map((_, index) => {
                    const labelLeft = index * TITLE_REPEAT_INTERVAL;
                    const remainingWidth = width - labelLeft;
                    const labelWidth = Math.min(TITLE_REPEAT_INTERVAL - 20, remainingWidth);

                    return (
                        <div
                            key={index}
                            className="absolute bottom-full mb-1 text-[10px] font-medium pointer-events-none overflow-hidden"
                            style={{
                                left: `${labelLeft}px`,
                                width: `${labelWidth}px`,
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {eventIcon && <span className="mr-1">{eventIcon}</span>}
                            {event.title}
                        </div>
                    );
                })}
            </>
        );
    }

    // For short bars: allow label to extend beyond bar width
    return (
        <div className="absolute bottom-full left-0 mb-1 text-[10px] font-medium pointer-events-none whitespace-nowrap">
            {eventIcon && <span className="mr-1">{eventIcon}</span>}
            {event.title}
        </div>
    );
});
EventLabels.displayName = 'EventLabels';

/**
 * Memoized tooltip content component
 */
const TooltipContentMemo = memo(({
    event,
    isDarkMode
}: {
    event: TimelineEvent;
    isDarkMode: boolean;
}) => (
    <TooltipContent
        side="top"
        sideOffset={8}
        className="max-w-xs p-3"
        style={{
            backgroundColor: isDarkMode ? '#1e293b' : '#f9fafb',
            color: isDarkMode ? '#f1f5f9' : '#111827',
            border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
        }}
    >
        <div className="font-semibold text-sm mb-1">{event.title}</div>
        <div className="text-xs mb-2" style={{ color: isDarkMode ? '#cbd5e1' : '#6b7280' }}>
            {event.displayDateRange}
        </div>
        <div className="text-xs line-clamp-3" style={{ color: isDarkMode ? '#e2e8f0' : '#4b5563' }}>
            {event.summary}
        </div>
    </TooltipContent>
));
TooltipContentMemo.displayName = 'TooltipContentMemo';

/**
 * Individual event marker (bar or pin) on the timeline
 * Memoized to prevent unnecessary re-renders
 */
export const EventMarker = memo(function EventMarker({
    event,
    startPosition,
    endPosition,
    rowIndex,
    onClick
}: EventMarkerProps) {
    const isDarkMode = useDarkMode();

    // State for custom tooltip positioning (for long bars)
    const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const isMobile = useIsMobile();

    // Memoize expensive calculations
    const {
        isSingleYear,
        width,
        height,
        pinSize,
        topPosition,
        color,
        eventIcon,
        isLongBar,
        titleRepeatCount,
        usesCustomTooltip
    } = useMemo(() => {
        const w = endPosition - startPosition;
        const isSingle = event.sortableYearStart === event.sortableYearEnd;

        // Responsive dimensions
        const baseH = event.importance === 'high' ? 18 : event.importance === 'low' ? 10 : 14;
        const basePS = event.importance === 'high' ? 18 : event.importance === 'low' ? 12 : 14;
        const h = isMobile ? Math.max(8, baseH - 4) : baseH;
        const ps = isMobile ? Math.max(10, basePS - 2) : basePS;

        const topBase = isMobile ? 24 : 30; // Changed from 30 to 24 for tighter mobile layout
        const rowHeight = isMobile ? 24 : 32;
        const top = topBase + rowIndex * rowHeight;

        const col = getSwimLaneColor(event.laneId);
        const icon = getEventIcon(event);
        const isLong = w > TITLE_REPEAT_INTERVAL;
        const repeatCount = isLong ? Math.ceil(w / TITLE_REPEAT_INTERVAL) : 1;
        const customTooltip = w > CUSTOM_TOOLTIP_THRESHOLD;

        return {
            isSingleYear: isSingle,
            width: w,
            height: h,
            pinSize: ps,
            topPosition: top,
            color: col,
            eventIcon: icon,
            isLongBar: isLong,
            titleRepeatCount: repeatCount,
            usesCustomTooltip: customTooltip
        };
    }, [event, startPosition, endPosition, rowIndex, isMobile]);

    // Memoize event handlers
    const handleClick = useMemo(() => () => onClick(event), [onClick, event]);

    const handleMouseMove = useMemo(() => (e: React.MouseEvent<HTMLDivElement>) => {
        if (usesCustomTooltip) {
            setTooltipPos({ x: e.clientX, y: e.clientY });
        }
    }, [usesCustomTooltip]);

    const handleMouseEnter = useMemo(() => () => {
        if (usesCustomTooltip) {
            setShowTooltip(true);
        }
    }, [usesCustomTooltip]);

    const handleMouseLeave = useMemo(() => () => {
        if (usesCustomTooltip) {
            setShowTooltip(false);
            setTooltipPos(null);
        }
    }, [usesCustomTooltip]);

    // Render single-year event (pin)
    if (isSingleYear || width < 10) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className="absolute cursor-pointer hover:z-10 hover:scale-102 group"
                        style={{
                            left: `${startPosition}px`,
                            top: `${topPosition}px`,
                            transform: 'translateY(-50%)',
                            transition: 'none'
                        }}
                        onClick={handleClick}
                    >
                        {/* Pin marker - centered on the date */}
                        <div
                            className="absolute flex-shrink-0"
                            style={{
                                left: '0',
                                top: '0',
                                transform: 'translateX(-50%)'
                            }}
                        >
                            {eventIcon ? (
                                <div
                                    className="flex items-center justify-center border-2 border-white shadow-md group-hover:shadow-lg rounded-full"
                                    style={{
                                        width: `${pinSize + 4}px`,
                                        height: `${pinSize + 4}px`,
                                        backgroundColor: color,
                                    }}
                                >
                                    <span className="text-sm">{eventIcon}</span>
                                </div>
                            ) : (
                                <div
                                    className="rounded-full border-2 border-white shadow-md group-hover:shadow-lg"
                                    style={{
                                        width: `${pinSize}px`,
                                        height: `${pinSize}px`,
                                        backgroundColor: color,
                                    }}
                                />
                            )}
                        </div>

                        {/* Event label */}
                        <div
                            className="absolute text-[10px] font-medium whitespace-nowrap pointer-events-none"
                            style={{
                                left: `${(eventIcon ? pinSize + 4 : pinSize) / 2 + 4}px`,
                                top: '0',
                                transform: 'translateY(0)'
                            }}
                        >
                            {event.title}
                        </div>
                    </div>
                </TooltipTrigger>
                <TooltipContentMemo event={event} isDarkMode={isDarkMode} />
            </Tooltip>
        );
    }

    // Render multi-year event with custom tooltip (for very long bars)
    if (usesCustomTooltip) {
        return (
            <>
                <div
                    className="absolute cursor-pointer hover:z-10 hover:scale-102 hover:brightness-110 group"
                    style={{
                        left: `${startPosition}px`,
                        top: `${topPosition}px`,
                        transform: `translateY(-50%)`,
                        width: `${width}px`,
                        transition: 'none'
                    }}
                    onClick={handleClick}
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <EventLabels
                        event={event}
                        eventIcon={eventIcon}
                        width={width}
                        isLongBar={isLongBar}
                        titleRepeatCount={titleRepeatCount}
                    />

                    {/* Event bar */}
                    <div
                        className="rounded border border-white/50 shadow-md group-hover:shadow-lg"
                        style={{
                            width: '100%',
                            height: `${height}px`,
                            backgroundColor: color,
                        }}
                    />
                </div>


                {/* Custom tooltip that follows mouse */}
                {showTooltip && tooltipPos && createPortal(
                    <div
                        className="fixed z-[9999] max-w-xs p-3 rounded-md shadow-lg pointer-events-none"
                        style={{
                            left: `${tooltipPos.x}px`,
                            top: `${tooltipPos.y}px`,
                            transform: 'translate(-50%, calc(-100% - 10px))',
                            backgroundColor: isDarkMode ? '#1e293b' : '#f9fafb',
                            color: isDarkMode ? '#f1f5f9' : '#111827',
                            border: `1px solid ${isDarkMode ? '#334155' : '#e5e7eb'}`,
                        }}
                    >
                        <div className="font-semibold text-sm mb-1">{event.title}</div>
                        <div className="text-xs mb-2" style={{ color: isDarkMode ? '#cbd5e1' : '#6b7280' }}>
                            {event.displayDateRange}
                        </div>
                        <div className="text-xs line-clamp-3" style={{ color: isDarkMode ? '#e2e8f0' : '#4b5563' }}>
                            {event.summary}
                        </div>
                    </div>,
                    document.body
                )}
            </>
        );
    }

    // Render multi-year event with standard tooltip
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <div
                    className="absolute cursor-pointer hover:z-10 hover:scale-102 hover:brightness-110 group"
                    style={{
                        left: `${startPosition}px`,
                        top: `${topPosition}px`,
                        transform: `translateY(-50%)`,
                        width: `${width}px`,
                        transition: 'none'
                    }}
                    onClick={handleClick}
                >
                    <EventLabels
                        event={event}
                        eventIcon={eventIcon}
                        width={width}
                        isLongBar={isLongBar}
                        titleRepeatCount={titleRepeatCount}
                    />

                    {/* Event bar */}
                    <div
                        className="rounded border border-white/50 shadow-md group-hover:shadow-lg"
                        style={{
                            width: '100%',
                            height: `${height}px`,
                            backgroundColor: color,
                        }}
                    />
                </div>
            </TooltipTrigger>
            <TooltipContentMemo event={event} isDarkMode={isDarkMode} />
        </Tooltip>
    );
});
